import { useCallback, useMemo } from 'react'
import Fuse from 'fuse.js'
import type { SearchItem } from 'const/searchItems'
import useSearchItems from './useSearchItems'

// Strips diacritics so 'credito' matches 'crédito'.
const stripAccents = (text: string): string =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

/** SearchItem with i18n-resolved label and tags applied. */
export interface ResolvedSearchItem extends SearchItem {
  label: string
  tags: string[]
}

/** Precomputed normalized fields used by the Fuse index. */
export interface IndexedItem {
  item: ResolvedSearchItem
  label: string
  tags: string[]
}

const MIN_MATCH_CHAR_LENGTH = 3
const MIN_FUZZY_TOKEN_LENGTH = 5
const MIN_SHARED_PREFIX_LENGTH = 5
const MIN_SHARED_PREFIX_RATIO = 0.8
const MIN_REVERSE_PREFIX_LENGTH = 4
const RESULT_LIMIT = 5
const TOKEN_TAG_SCORE = 0.1
const FUZZY_SCORE_OFFSET = 1
const FILLER_WORDS = new Set(['and', 'get', 'mi', 'my', 'y'])

// Lower score = better match. Concrete label matches always beat tag matches.
const SCORE = {
  EXACT_LABEL: 0,
  LABEL_PREFIX: 1,
  TAG_MATCH: 2
} as const

const fuseOptions: Fuse.IFuseOptions<IndexedItem> = {
  threshold: 0.25,
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: MIN_FUZZY_TOKEN_LENGTH,
  keys: [
    { name: 'label', weight: 3 },
    { name: 'tags', weight: 1 }
  ]
}

const buildIndex = (items: ResolvedSearchItem[]): IndexedItem[] =>
  items.map((item) => ({
    item,
    label: stripAccents(item.label).toLowerCase(),
    tags: item.tags.map((tag) => stripAccents(tag).toLowerCase())
  }))

const tokenize = (query: string): string[] =>
  Array.from(
    new Set(
      query
        .split(/[^a-z0-9]+/)
        .filter(
          (token) =>
            token.length >= MIN_MATCH_CHAR_LENGTH && !FILLER_WORDS.has(token)
        )
    )
  )

const hasSharedPrefix = (left: string, right: string): boolean => {
  if (left.startsWith(right)) return true
  if (right.startsWith(left) && left.length >= MIN_REVERSE_PREFIX_LENGTH) {
    return true
  }

  let prefixLength = 0
  const maxLength = Math.min(left.length, right.length)
  while (
    prefixLength < maxLength &&
    left[prefixLength] === right[prefixLength]
  ) {
    prefixLength += 1
  }
  return (
    prefixLength >= MIN_SHARED_PREFIX_LENGTH &&
    prefixLength / maxLength >= MIN_SHARED_PREFIX_RATIO
  )
}

const scoreToken = (
  labelWords: string[],
  tags: string[],
  token: string
): number | null => {
  if (labelWords.some((word) => hasSharedPrefix(word, token))) {
    return SCORE.LABEL_PREFIX
  }
  if (tags.some((tag) => hasSharedPrefix(tag, token))) {
    return SCORE.TAG_MATCH
  }
  return null
}

const scoreItem = (
  { label, tags }: IndexedItem,
  query: string
): number | null => {
  if (label === query) return SCORE.EXACT_LABEL
  if (
    label.startsWith(query) ||
    label.split(/\s+/).some((word) => word.startsWith(query))
  ) {
    return SCORE.LABEL_PREFIX
  }
  /*
   * Tag prefixes cast a wide net (e.g. "hi" → "history"), so require the same
   * minimum length Fuse uses. Exact tag equality still wins at any length.
   */
  if (tags.some((tag) => tag === query)) return SCORE.TAG_MATCH
  if (
    query.length >= MIN_MATCH_CHAR_LENGTH &&
    tags.some((tag) => tag.startsWith(query))
  ) {
    return SCORE.TAG_MATCH
  }

  const tokens = tokenize(query)
  if (tokens.length === 0) return null
  const labelWords = label.split(/\s+/)
  const tokenScores = tokens.map((token) => scoreToken(labelWords, tags, token))
  if (tokenScores.some((score) => score === null)) return null
  return tokenScores.reduce<number>((total, score) => total + (score ?? 0), 0)
}

const deterministicMatches = (
  index: IndexedItem[],
  query: string
): ResolvedSearchItem[] =>
  index
    .flatMap((entry) => {
      const score = scoreItem(entry, query)
      return score === null ? [] : [{ item: entry.item, score }]
    })
    .sort((a, b) => a.score - b.score)
    .map(({ item }) => item)

const getTokenMatches = (
  index: IndexedItem[],
  fuse: Fuse<IndexedItem>,
  token: string
): Map<string, number> => {
  const matches = new Map<string, number>()

  index.forEach(({ item, label, tags }) => {
    const labelWords = label.split(/\s+/)
    if (labelWords.some((word) => hasSharedPrefix(word, token))) {
      matches.set(item.id, 0)
    } else if (tags.some((tag) => hasSharedPrefix(tag, token))) {
      matches.set(item.id, TOKEN_TAG_SCORE)
    }
  })

  if (token.length >= MIN_FUZZY_TOKEN_LENGTH) {
    fuse.search(token).forEach(({ item, score }) => {
      if (!matches.has(item.item.id)) {
        matches.set(item.item.id, FUZZY_SCORE_OFFSET + (score ?? 1))
      }
    })
  }

  return matches
}

const fuzzyTokenMatches = (
  index: IndexedItem[],
  fuse: Fuse<IndexedItem>,
  query: string
): ResolvedSearchItem[] => {
  const tokens = tokenize(query)
  if (tokens.length === 0) return []

  const matchesByToken = tokens.map((token) =>
    getTokenMatches(index, fuse, token)
  )

  return index
    .flatMap(({ item }) => {
      const tokenScores = matchesByToken.flatMap((matches) => {
        const score = matches.get(item.id)
        return score === undefined ? [] : [score]
      })
      if (tokenScores.length === 0) return []
      const score = tokenScores.reduce(
        (total, tokenScore) => total + tokenScore,
        0
      )
      return [{ item, matchedTokenCount: tokenScores.length, score }]
    })
    .sort(
      (left, right) =>
        right.matchedTokenCount - left.matchedTokenCount ||
        left.score - right.score
    )
    .map(({ item }) => item)
}

const useSemanticSearch = () => {
  const { items, isLoading, isError } = useSearchItems()

  const { index, fuse } = useMemo(() => {
    const indexedItems = buildIndex(items)
    return { index: indexedItems, fuse: new Fuse(indexedItems, fuseOptions) }
  }, [items])

  const search = useCallback(
    (query: string): ResolvedSearchItem[] => {
      const normalized = stripAccents(query.trim()).toLowerCase()
      if (!normalized) return []

      const results: ResolvedSearchItem[] = []
      const seen = new Set<string>()
      const push = (item: ResolvedSearchItem) => {
        if (results.length < RESULT_LIMIT && !seen.has(item.id)) {
          seen.add(item.id)
          results.push(item)
        }
      }

      deterministicMatches(index, normalized).forEach(push)

      if (
        results.length < RESULT_LIMIT &&
        normalized.length >= MIN_MATCH_CHAR_LENGTH
      ) {
        fuzzyTokenMatches(index, fuse, normalized).forEach(push)
      }

      return results
    },
    [index, fuse]
  )

  return { search, isLoading, isError }
}

export default useSemanticSearch
